import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { Platform } from '@ionic/angular';
import {
  combineLatest,
  Observable,
  of,
  Subscription,
  throwError,
  timer,
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SessionQuery } from './core/state-mangement/session/session.query';
import { BackendService } from './core/state-mangement/backend/backend.service';
import { BackendQuery } from './core/state-mangement/backend/backend.query';
import { SessionService } from './core/state-mangement/session/session.service';
import { SettingsService } from './core/state-mangement/settings/settings.service';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { SideMenuQuery } from './core/state-mangement/side-menu/side-menu.query';
import { SettingsBackendService } from './core/services/settings/settings-backend.service';
import { SqlSettingsService } from './core/state-mangement/sql-settings/sql-settings.service';
import { BadgeService } from './core/state-mangement/badge/badge.service';
import { DayInfoService } from './core/state-mangement/day-info/day-info.service';
import { AbsencePlanerService } from './core/state-mangement/absence-planer/absence-planer.service';
import { AbsencePlanerQuery } from './core/state-mangement/absence-planer/absence-planer.query';
import { SideMenuEnum } from './core/enums/side-menu.enum';
import { TokenService } from './core/services/token.service';
import { HelpManualService } from './core/services/help-manual.service';
import { TextKeys } from './core/i18n/text-keys';
import { SideMenuService } from './core/state-mangement/side-menu/side-menu.service';
import { AppConfig } from './core/services/config.service';
import { EncryptionBackendService } from './core/services/encryption/encryption-backend.service';
import { ToastService } from './core/services/toast-service';
import { LangaugeService } from './core/services/language.service';
import tippy from 'tippy.js';
import { SettingsKvInitService } from './core/state-mangement/settings/settings-kv-init.service';
import { BadgeKvService } from './core/services/badge/badge-kv.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  // appPages$ = this.sideMenuQuery
  //   .selectSettings()
  //   .pipe(
  //     distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  //   );

  requestedAbsences$: Observable<number>;
  logedIn$: Observable<boolean>;
  online$: Observable<boolean>;
  servicePause$: Observable<boolean>;
  userName$: Observable<string>;
  onNativeLoginPage$: Observable<boolean>;
  subscriptions = new Subscription();
  texts = TextKeys;
  settingsSaveTimer = timer(500);
  lastSnapshot = '';
  sideMenuItems = SideMenuEnum;

  constructor(
    private platform: Platform,
    private translate: TranslateService,
    private sessionQuery: SessionQuery,
    private sessionService: SessionService,
    private router: Router,
    private backendService: BackendService,
    private sideMenuQuery: SideMenuQuery,
    private settingsBackendService: SettingsBackendService,
    private backendQuery: BackendQuery,
    private settingsService: SettingsService,
    private languageService: LangaugeService,
    private sqlSettingsService: SqlSettingsService,
    private absencePlanerService: AbsencePlanerService,
    private absencePlanerQuery: AbsencePlanerQuery,
    private badgeService: BadgeService,
    private dayInfoService: DayInfoService,
    private encryptionService: EncryptionBackendService,
    private toastService: ToastService,
    private settingsKvInit: SettingsKvInitService,
    private badgeKvService: BadgeKvService,
    private sideMenuService: SideMenuService
  ) {
    this.initializeApp();
  }

  ngOnInit() {
    this.registerStoreSaveInSQL();
    this.logedIn$ = this.sessionQuery.successful$;
    this.online$ = this.backendQuery.online$;
    this.servicePause$ = this.backendQuery.servicePause$;
    this.userName$ = this.sessionQuery.numberAndName$;

    this.requestedAbsences$ = this.absencePlanerQuery.selectAbsenceCount();

    this.subscriptions.add(
      this.sessionQuery.chronoVersion$.subscribe((value) => {
        this.badgeService.updateSettingsWhenChronoVersionChanges();
        this.dayInfoService.updateSettingsWhenChronoVersionChanges();
      })
    );

    this.onNativeLoginPage$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((url: RouterEvent) => url.url.indexOf('native-app-login') !== -1)
    );

    this.sessionService.getVisumTimeSetting();

    // Sobald der Login erfolgreich ist
    this.sessionQuery.successful$
      .pipe(
        filter((s) => !!s),
        take(1)
      )
      .subscribe((s) => {
        this.settingsKvInit.initOnLogin();
        this.badgeKvService.loadAndApply();
        this.sideMenuService.initMenuAndRedirect();
      });

    this.requestedAbsences$ = this.absencePlanerQuery.selectAbsenceCount();

    // NEU: Absenzen immer bei Login (re)laden
    this.subscriptions.add(
      this.sessionQuery.successful$
        .pipe(distinctUntilChanged()) // reagiert auf true/false Wechsel
        .subscribe((loggedIn) => {
          if (loggedIn) {
            this.absencePlanerService.getRequestedAbsences();
          } else {
            // Optional: auf 0 setzen/clearen, falls du so einen Service-Call hast
            // this.absencePlanerService.clearRequestedAbsences();
          }
        })
    );

    // Falls du die KV-Init nur einmalig möchtest, lass dein take(1) da:
    this.sessionQuery.successful$
      .pipe(filter(Boolean), take(1))
      .subscribe(() => {
        this.settingsKvInit.initOnLogin();
        this.badgeKvService.loadAndApply();
      });
  }

  ngAfterViewInit(): void {
    tippy('[data-tippy-content]', {
      theme: 'admiatippy',
      animation: 'fade',
      duration: 100,
      delay: [50, 0],
    });
  }

  initializeApp() {
    this.platform.ready().then(() => {
      this.backendService.checkBackendStatus();
      this.translate.setDefaultLang('de');
      this.settingsService.setThemeModeFromStore();
      this.settingsService.setThemeFromStore();
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  clicked(page) {
    // Elternknoten nicht "klickbar" behandeln
    if (page?.children?.length) return;
    console.log(page);

    if (page.name == SideMenuEnum.ExpenseDuplicateReport) {
      this.expenseDuplicateReportNav();
      return;
    }
    if (!page.urlActive) {
      this.sessionService.logout();
    }
  }

  expenseDuplicateReportNav() {
    const baseReportUrl =
      AppConfig.settings.backend.url.replace('/api', '/report') +
      'reportviewer';

    const fileName = 'Spesen Duplikate';
    const fileUrl = AppConfig.settings.backend.url.replace('/api/', '');

    const params = [`pSpese;0`, `Pfad;${fileUrl}`];

    const parameters = JSON.stringify({
      title: 'Spesen Duplikate',
      reportName: 'SpesenDoubletten.mrx',
      fileName,
      parameters: params,
    });

    this.subscriptions.add(
      this.encryptionService
        .getEncryptedString(parameters)
        .subscribe((value) => {
          window.open(
            baseReportUrl + '?parameter=' + encodeURIComponent(value.value),
            '_blank'
          );
        })
    );
  }

  expandClicked(event) {
    event.stopPropagation();
  }

  settingsNav() {
    this.router
      .navigate(['settings'])
      .catch((error) =>
        this.toastService.presentErrorToast({ message: error?.error.message })
      );
  }

  helpNav() {
    window.open(HelpManualService.getUrl(''), '_blank');
  }

  support() {
    window.open('https://get.teamviewer.com/gtjmd5s', '_blank');
  }

  private manageStoreSQLOnce() {
    if (
      (!this.sessionQuery.getPersonID() ||
        this.sessionQuery.getPersonID() === 0) &&
      (!this.sessionQuery.getAdmiaUserID() ||
        this.sessionQuery.getAdmiaUserID() === 0)
    ) {
      return of(null);
    }

    return this.settingsBackendService
      .getSettings(
        this.sessionQuery.getPersonID(),
        this.sessionQuery.getAdmiaUserID()
      )
      .pipe(
        switchMap((savedSettings) => {
          if (
            (!this.sessionQuery.getPerson()?.id ||
              this.sessionQuery.getPerson()?.id === 0) &&
            (!this.sessionQuery.getAdmiaUserID() ||
              this.sessionQuery.getAdmiaUserID() === 0)
          ) {
            return of(null);
          }

          let migrated = false;
          if (this.sqlSettingsService.checkMigrationNeeded(savedSettings)) {
            // Migration wie gehabt – aber NUR lesen/anwenden
            if (!savedSettings?.value || savedSettings.value === '') {
              savedSettings.value =
                this.sqlSettingsService.getCurrentSettingsString();
            }
            savedSettings =
              this.sqlSettingsService.migrateSettings(savedSettings);
            this.sqlSettingsService.updateSettingsFromDB(savedSettings, true);
            migrated = true;
          } else {
            this.sqlSettingsService.updateSettingsFromDB(savedSettings);
          }

          // WICHTIG: KEIN Zurückschreiben mehr hier!
          return of(null);
        }),
        catchError((error) => {
          console.error(
            'An error occurred during the store SQL management',
            error
          );
          return throwError(() => new Error('Error in manageStoreSQLOnce'));
        })
      );
  }

  private registerStoreSaveInSQL() {
    this.subscriptions.add(
      this.settingsSaveTimer
        .pipe(
          take(1), // << nur einmal
          filter(() => this.isSessionValid()),
          filter(() => TokenService.token && TokenService.token !== ''),
          tap(() => this.performSideMenuSettingsSave()),
          switchMap(() => this.manageStoreSQLOnce()),
          catchError((error) => {
            console.error('Error during settings management', error);
            return of(null);
          })
        )
        .subscribe()
    );
  }

  private isSessionValid(): boolean {
    const personId = this.sessionQuery.getPersonID();
    const admiaUserId = this.sessionQuery.getAdmiaUserID();
    return (personId && personId !== 0) || (admiaUserId && admiaUserId !== 0);
  }

  private performSideMenuSettingsSave(): void {
    // nur noch Absenzen anstossen, KEIN SideMenu auto-save
    // this.absencePlanerService.getRequestedAbsences();
  }
}
